import React from "react";
import styled from "styled-components";
import {
  BarChart3,
  Eye,
  Lightbulb,
  TrendingUp,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Clock,
  Database,
  PieChart,
  LineChart,
} from "lucide-react";
import VegaChartRenderer from "./VegaChartRenderer";
import TableChartRenderer from "./TableChartRenderer";
import ChartDataService, {
  ChartData,
  Dashboard,
  DashboardChart,
} from "../services/chartDataService";
import { rawColors } from "../design-system/tokens/colors";

const StepContainer = styled.div`
  // background: rgba(255, 255, 255, 0.05);
  // border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  // padding: 1.5rem;
  margin-bottom: 1rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  margin-top: 2rem;
`;

const StepIcon = styled.div<{ status: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => {
    switch (props.status) {
      case "completed":
        return "rgba(34, 197, 94, 0.2)";
      case "processing":
        return "rgba(59, 130, 246, 0.2)";
      case "failed":
        return "rgba(239, 68, 68, 0.2)";
      default:
        return "rgba(255, 255, 255, 0.1)";
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case "completed":
        return "#22c55e";
      case "processing":
        return "#3b82f6";
      case "failed":
        return "#ef4444";
      default:
        return "rgba(255, 255, 255, 0.8)";
    }
  }};
`;

const StepTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${rawColors.text.white};
  margin: 0;
`;

const StepStatus = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: ${(props) => {
    switch (props.status) {
      case "completed":
        return "#22c55e";
      case "processing":
        return "#3b82f6";
      case "failed":
        return "#ef4444";
      default:
        return "rgba(255, 255, 255, 0.6)";
    }
  }};
`;

const StepContent = styled.div`
  // color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  width: 100%;
  overflow: hidden; /* Prevent content overflow */
  box-sizing: border-box;
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const DataCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
`;

const DataCardTitle = styled.h4`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${rawColors.text.white};
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DataCardValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #3b82f6;
`;

const InsightCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
`;

const InsightTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: ${rawColors.text.white};
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InsightDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 0.75rem 0;
  line-height: 1.5;
`;

const InsightMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
`;

const ConfidenceBadge = styled.span<{ confidence: number }>`
  background: ${(props) => {
    if (props.confidence >= 0.8) return "rgba(34, 197, 94, 0.2)";
    if (props.confidence >= 0.6) return "rgba(245, 158, 11, 0.2)";
    return "rgba(239, 68, 68, 0.2)";
  }};
  color: ${(props) => {
    if (props.confidence >= 0.8) return "#22c55e";
    if (props.confidence >= 0.6) return "#f59e0b";
    return "#ef4444";
  }};
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const DashboardCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  padding-bottom: 0;
  margin-bottom: 0.75rem;
`;

const DashboardTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: ${rawColors.text.white};
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ChartList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ChartBadge = styled.span`
  background: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const NextStepCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
`;

const NextStepTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: ${rawColors.text.white};
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const NextStepDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 0.75rem 0;
  line-height: 1.5;
`;

const NextStepMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
`;

const ImportanceBadge = styled.span<{ importance: number }>`
  background: ${(props) => {
    if (props.importance >= 0.8) return "rgba(239, 68, 68, 0.2)";
    if (props.importance >= 0.6) return "rgba(245, 158, 11, 0.2)";
    return "rgba(34, 197, 94, 0.2)";
  }};
  color: ${(props) => {
    if (props.importance >= 0.8) return "#ef4444";
    if (props.importance >= 0.6) return "#f59e0b";
    return "#22c55e";
  }};
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  overflow-y: auto;
  margin-top: 1rem;
  max-width: 1008px;
  width: 100%;
  max-height: 400px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);

  /* Critical: Prevent table from affecting parent layout */
  width: 100%;
  box-sizing: border-box;

  /* Force container to respect parent boundaries */
  min-width: 0;
  flex-shrink: 0;

  /* Ensure table doesn't break out of container */
  position: relative;

  /* Ensure proper scrolling behavior */
  display: block;

  /* Add subtle scrollbar styling for both directions */
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }

  /* Responsive design for smaller screens */
  @media (max-width: 768px) {
    margin-top: 0.5rem;
    max-height: 300px;

    &::-webkit-scrollbar {
      height: 6px;
      width: 6px;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 600px;
  border-collapse: collapse;
  font-size: 0.9rem;
  white-space: nowrap;
  table-layout: auto;

  @media (max-width: 768px) {
    min-width: 500px;
    font-size: 0.8rem;
  }
`;

const Th = styled.th`
  background: rgba(255, 255, 255, 0.15);
  color: ${rawColors.text.white};
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap;
  min-width: 120px;
  position: sticky;
  top: 0;
  z-index: 1;
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    padding: 0.5rem;
    min-width: 100px;
    font-size: 0.8rem;
  }
`;

const Td = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
  min-width: 120px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0.5rem;
    min-width: 100px;
  }
`;

const TableWrapper = styled.div`
  margin-top: 1rem;
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  min-width: 0;
  flex-shrink: 0;
  position: relative;
`;

const ChartsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
`;

const ChartWrapper = styled.div<{ isFullWidth?: boolean }>`
  ${(props) =>
    props.isFullWidth
      ? `
      width: 100%;
      margin-bottom: 1rem;
      //min-height: 550px;
    `
      : `
      width: calc(50% - 0.5rem);
      margin-bottom: 1rem;
      //min-height: 550px;
      
      @media (max-width: 768px) {
        width: 100%;
      }
    `}
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: rgba(255, 255, 255, 0.6);
`;

interface AnalysisStepRendererProps {
  step: string;
  status: string;
  data?: any;
}

const AnalysisStepRenderer: React.FC<AnalysisStepRendererProps> = ({
  step,
  status,
  data,
}) => {
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const [chartDataMap, setChartDataMap] = React.useState<
    Map<string, ChartData>
  >(new Map());
  const [loadingCharts, setLoadingCharts] = React.useState<Set<string>>(
    new Set()
  );
  const [visibleRows, setVisibleRows] = React.useState<number>(10);
  const [isLoadingMore, setIsLoadingMore] = React.useState<boolean>(false);
  const chartDataService = ChartDataService.getInstance();
  const hasFetchedDataRef = React.useRef<Set<string>>(new Set());

  const handleWheel = (e: React.WheelEvent) => {
    if (tableContainerRef.current) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        tableContainerRef.current.scrollLeft += e.deltaX;
      }
    }
  };

  const handleTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = target;

    if (
      scrollHeight - scrollTop - clientHeight < 50 &&
      !isLoadingMore &&
      visibleRows < 100
    ) {
      loadMoreRows();
    }
  };

  const loadMoreRows = React.useCallback(() => {
    if (!data?.data || isLoadingMore || visibleRows >= 100) return;

    setIsLoadingMore(true);

    setTimeout(() => {
      setVisibleRows((prev) => {
        const newCount = Math.min(prev + 10, Math.min(data.data.length, 100));
        setIsLoadingMore(false);
        return newCount;
      });
    }, 300);
  }, [data?.data, isLoadingMore, visibleRows]);

  const fetchDashboardData = React.useCallback(
    async (fileId: string, dashboard: Dashboard) => {
      try {
        const chartDataArray = await chartDataService.fetchDashboardData(
          fileId,
          dashboard,
          (chartId: string, loading: boolean) => {
            setLoadingCharts((prev) => {
              const newSet = new Set(prev);
              if (loading) {
                newSet.add(chartId);
              } else {
                newSet.delete(chartId);
              }
              return newSet;
            });
          }
        );

        setChartDataMap((prev) => {
          const newChartDataMap = new Map(prev);
          chartDataArray.forEach((chartData) => {
            newChartDataMap.set(chartData.id, chartData);
          });
          return newChartDataMap;
        });
      } catch (error) {
        console.error(`❌ Failed to fetch dashboard data:`, error);
      }
    },
    [chartDataService]
  );

  React.useEffect(() => {
    if (step === "dashboards" && status === "completed" && data?.dashboards) {
      const fileId = sessionStorage.getItem("uploadedFileId");
      if (fileId) {
        const dataKey = JSON.stringify(data.dashboards.map((d) => d.id).sort());

        if (!hasFetchedDataRef.current.has(dataKey)) {
          hasFetchedDataRef.current.add(dataKey);
          data.dashboards.forEach((dashboard: Dashboard) => {
            fetchDashboardData(fileId, dashboard);
          });
        }
      }
    }

    return () => {
      if (step !== "dashboards") {
        hasFetchedDataRef.current.clear();
        setChartDataMap(new Map());
        setLoadingCharts(new Set());
      }
    };
  }, [step, status, data, fetchDashboardData]);

  React.useEffect(() => {
    if (step === "preview") {
      setVisibleRows(10);
      setIsLoadingMore(false);
    }
  }, [step]);

  const getStepIcon = (stepName: string) => {
    switch (stepName) {
      case "preview":
        return <Eye size={20} />;
      case "insights":
        return <Lightbulb size={20} />;
      case "dashboards":
        return <BarChart3 size={20} />;
      case "next-steps":
        return <ArrowRight size={20} />;
      case "executing-sql":
        return <Database size={20} />;
      default:
        return <TrendingUp size={20} />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle size={16} />;
      case "processing":
        return <Clock size={16} />;
      case "failed":
        return <AlertCircle size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const getChartIcon = (chartType: string) => {
    switch (chartType?.toLowerCase()) {
      case "bar":
        return <BarChart3 size={12} />;
      case "pie":
        return <PieChart size={12} />;
      case "line":
        return <LineChart size={12} />;
      default:
        return <BarChart3 size={12} />;
    }
  };

  const renderPreviewData = () => {
    if (!data) return null;

    return (
      <StepContent>
        <DataGrid>
          <DataCard>
            <DataCardTitle>Total Rows</DataCardTitle>
            <DataCardValue>{data.total_rows || 0}</DataCardValue>
          </DataCard>
          <DataCard>
            <DataCardTitle>Columns</DataCardTitle>
            <DataCardValue>{data.columns?.length || 0}</DataCardValue>
          </DataCard>
          <DataCard>
            <DataCardTitle>Page</DataCardTitle>
            <DataCardValue>{data.page || 0}</DataCardValue>
          </DataCard>
        </DataGrid>

        {data.columns && data.columns.length > 0 && (
          <div style={{ marginTop: "1rem" }}>
            <h4 style={{ color: "white", marginBottom: "0.5rem" }}>Columns:</h4>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {data.columns.map((column: string, index: number) => (
                <span
                  key={index}
                  style={{
                    background: "rgba(59, 130, 246, 0.2)",
                    color: "#3b82f6",
                    padding: "0.25rem 0.5rem",
                    borderRadius: "12px",
                    fontSize: "0.75rem",
                  }}
                >
                  {column}
                </span>
              ))}
            </div>
          </div>
        )}

        {data.data && data.data.length > 0 && (
          <TableWrapper>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "0.5rem",
              }}
            >
              <h4 style={{ color: "white", margin: 0 }}>Sample Data:</h4>
              <div
                style={{ display: "flex", alignItems: "center", gap: "1rem" }}
              >
                <span
                  style={{
                    fontSize: "0.8rem",
                    color: "rgba(255, 255, 255, 0.6)",
                  }}
                >
                  Showing {visibleRows} of {Math.min(data.data.length, 100)}{" "}
                  rows
                  {data.data.length > 100 && ` (max 100)`}
                </span>
                <span
                  style={{
                    fontSize: "0.8rem",
                    color: "rgba(255, 255, 255, 0.6)",
                    fontStyle: "italic",
                  }}
                >
                  ↔ Scroll horizontally • ↕ Scroll vertically for more
                </span>
              </div>
            </div>
            <TableContainer
              ref={tableContainerRef}
              onWheel={handleWheel}
              onScroll={handleTableScroll}
            >
              <Table>
                <thead>
                  <tr>
                    {Object.keys(data.data[0] || {}).map((key) => (
                      <Th key={key} title={key}>
                        {key}
                      </Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.data
                    .slice(0, visibleRows)
                    .map((row: any, index: number) => (
                      <tr key={index}>
                        {Object.values(row).map(
                          (value: any, cellIndex: number) => (
                            <Td key={cellIndex} title={String(value)}>
                              {String(value)}
                            </Td>
                          )
                        )}
                      </tr>
                    ))}
                </tbody>
              </Table>
              {isLoadingMore && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "1rem",
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: "0.9rem",
                  }}
                >
                  <Clock
                    size={16}
                    className="animate-spin"
                    style={{ marginRight: "0.5rem" }}
                  />
                  Loading more rows...
                </div>
              )}
              {!isLoadingMore &&
                visibleRows < Math.min(data.data.length, 100) && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "1rem",
                      borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <button
                      onClick={loadMoreRows}
                      style={{
                        background: "rgba(59, 130, 246, 0.2)",
                        color: "#3b82f6",
                        border: "1px solid rgba(59, 130, 246, 0.3)",
                        borderRadius: "6px",
                        padding: "0.5rem 1rem",
                        fontSize: "0.9rem",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                          "rgba(59, 130, 246, 0.3)";
                        e.currentTarget.style.borderColor =
                          "rgba(59, 130, 246, 0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          "rgba(59, 130, 246, 0.2)";
                        e.currentTarget.style.borderColor =
                          "rgba(59, 130, 246, 0.3)";
                      }}
                    >
                      <ArrowRight size={14} />
                      Load More Rows
                    </button>
                  </div>
                )}
              {visibleRows >= Math.min(data.data.length, 100) &&
                data.data.length > 100 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "1rem",
                      borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                      color: "rgba(255, 255, 255, 0.6)",
                      fontSize: "0.9rem",
                    }}
                  >
                    <span>Maximum of 100 rows displayed</span>
                  </div>
                )}
            </TableContainer>
          </TableWrapper>
        )}
      </StepContent>
    );
  };

  const renderInsightsData = () => {
    if (!data?.insights) return null;

    return (
      <StepContent>
        <DataGrid>
          <DataCard>
            <DataCardTitle>Total Insights</DataCardTitle>
            <DataCardValue>{data.insights.length}</DataCardValue>
          </DataCard>
        </DataGrid>

        {data.insights.map((insight: any, index: number) => (
          <InsightCard key={index}>
            <InsightTitle>
              <Lightbulb size={16} />
              {insight.title}
            </InsightTitle>
            <InsightDescription>{insight.description}</InsightDescription>
            <InsightMeta>
              <ConfidenceBadge confidence={insight.confidence || 0}>
                {Math.round((insight.confidence || 0) * 100)}% Confidence
              </ConfidenceBadge>
              <span>Category: {insight.category}</span>
              <span>Relevance: {insight.relevance}</span>
            </InsightMeta>

            {insight.visualization_data && insight.visualization_type && (
              <div style={{ marginTop: "1rem" }}>
                <VegaChartRenderer
                  title={`${insight.title} - Visualization`}
                  description={insight.description}
                  chartType={insight.visualization_type}
                  data={insight.visualization_data}
                  xField={insight.x_axis}
                  yField={insight.y_axis}
                  colorField={insight.color_field}
                  width={400}
                  height={250}
                />
              </div>
            )}
          </InsightCard>
        ))}
      </StepContent>
    );
  };

  const renderDashboardsData = () => {
    if (!data?.dashboards) return null;

    return (
      <StepContent>
        <DataGrid>
          <DataCard>
            <DataCardTitle>Total Dashboards</DataCardTitle>
            <DataCardValue>{data.dashboards.length}</DataCardValue>
          </DataCard>
          <DataCard>
            <DataCardTitle>Total Charts</DataCardTitle>
            <DataCardValue>
              {data.dashboards.reduce(
                (total: number, dashboard: any) =>
                  total + (dashboard.charts?.length || 0),
                0
              )}
            </DataCardValue>
          </DataCard>
        </DataGrid>

        {data.dashboards.map((dashboard: any, index: number) => (
          <DashboardCard key={index}>
            <DashboardTitle>
              <BarChart3 size={16} />
              {dashboard.title}
            </DashboardTitle>
            <p
              style={{
                color: "rgba(255, 255, 255, 0.7)",
                margin: "0 0 0.75rem 0",
              }}
            >
              {dashboard.description}
            </p>

            {dashboard.charts && dashboard.charts.length > 0 && (
              <ChartList>
                {dashboard.charts.map((chart: any, chartIndex: number) => (
                  <ChartBadge key={chartIndex}>
                    {getChartIcon(chart.chart_type)}
                    {chart.title}
                  </ChartBadge>
                ))}
              </ChartList>
            )}

            {dashboard.charts && dashboard.charts.length > 0 && (
              <ChartsGrid>
                {(() => {
                  const totalCharts = dashboard.charts.length;
                  const isOdd = totalCharts % 2 === 1;

                  return dashboard.charts.map(
                    (chart: DashboardChart, chartIndex: number) => {
                      const chartData = chartDataMap.get(chart.id);
                      const isLoading = loadingCharts.has(chart.id);

                      const isFullWidth = isOdd && chartIndex === 0;

                      const renderChart = () => {
                        if (isLoading) {
                          return (
                            <div
                              style={{
                                padding: "2rem",
                                textAlign: "center",
                                color: "rgba(255, 255, 255, 0.6)",
                                background: "rgba(255, 255, 255, 0.05)",
                                borderRadius: "8px",
                                border: "1px solid rgba(255, 255, 255, 0.1)",
                              }}
                            >
                              <Clock
                                size={20}
                                className="animate-spin"
                                style={{ marginBottom: "0.5rem" }}
                              />
                              <div>Loading chart data...</div>
                            </div>
                          );
                        } else if (chartData?.error) {
                          return (
                            <div
                              style={{
                                padding: "1rem",
                                color: "#ef4444",
                                background: "rgba(239, 68, 68, 0.1)",
                                borderRadius: "8px",
                                border: "1px solid rgba(239, 68, 68, 0.3)",
                              }}
                            >
                              ❌ Error loading chart: {chartData.error}
                            </div>
                          );
                        } else if (
                          chartData?.data &&
                          chartData.data.length > 0
                        ) {
                          return (
                            <VegaChartRenderer
                              key={`${chart.id}-${chartData.data.length}`}
                              title={chart.title}
                              description={chart.description}
                              chartType={
                                chart.chart_type as
                                  | "bar"
                                  | "line"
                                  | "pie"
                                  | "scatter"
                                  | "area"
                                  | "histogram"
                                  | "table"
                              }
                              data={chartData.data}
                              xField={chartData.xField}
                              yField={chartData.yField}
                              colorField={chartData.colorField}
                              width={
                                chart.position?.width ||
                                (chart.chart_type === "pie" ? 750 : 400)
                              }
                              height={
                                chart.position?.height ||
                                (chart.chart_type === "pie" ? 500 : 250)
                              }
                            />
                          );
                        } else if (chart.chart_type === "table") {
                          return (
                            <TableChartRenderer
                              key={`${chart.id}-table-${
                                chartData?.data?.length || 0
                              }`}
                              title={chart.title}
                              description={chart.description}
                              data={chartData?.data || []}
                              loading={isLoading}
                              error={chartData?.error}
                            />
                          );
                        } else {
                          return (
                            <div
                              style={{
                                padding: "1rem",
                                color: "rgba(255, 255, 255, 0.6)",
                                background: "rgba(255, 255, 255, 0.05)",
                                borderRadius: "8px",
                                border: "1px solid rgba(255, 255, 255, 0.1)",
                              }}
                            >
                              📊 Chart: {chart.title}
                              <div
                                style={{
                                  fontSize: "0.9rem",
                                  marginTop: "0.5rem",
                                }}
                              >
                                No data available
                              </div>
                            </div>
                          );
                        }
                      };

                      return (
                        <ChartWrapper key={chart.id} isFullWidth={isFullWidth}>
                          {renderChart()}
                        </ChartWrapper>
                      );
                    }
                  );
                })()}
              </ChartsGrid>
            )}
          </DashboardCard>
        ))}
      </StepContent>
    );
  };

  const renderNextStepsData = () => {
    if (!data?.next_steps) return null;

    return (
      <StepContent>
        <DataGrid>
          <DataCard>
            <DataCardTitle>Total Next Steps</DataCardTitle>
            <DataCardValue>{data.next_steps.length}</DataCardValue>
          </DataCard>
        </DataGrid>

        {data.next_steps.map((step: any, index: number) => (
          <NextStepCard key={index}>
            <NextStepTitle>
              <ArrowRight size={16} />
              {step.title}
            </NextStepTitle>
            <NextStepDescription>{step.description}</NextStepDescription>
            <NextStepMeta>
              <ImportanceBadge importance={step.importance || 0}>
                {step.importance >= 0.8
                  ? "High"
                  : step.importance >= 0.6
                  ? "Medium"
                  : "Low"}{" "}
                Priority
              </ImportanceBadge>
              <span>Action: {step.action_type}</span>
            </NextStepMeta>
          </NextStepCard>
        ))}
      </StepContent>
    );
  };

  const renderStepContent = () => {
    if (status === "processing") {
      return (
        <LoadingSpinner>
          <Clock size={20} className="animate-spin" />
          <span style={{ marginLeft: "0.5rem" }}>Processing...</span>
        </LoadingSpinner>
      );
    }

    if (status === "failed") {
      return (
        <StepContent>
          <p style={{ color: "#ef4444" }}>
            ❌ Step failed: {data?.error || "Unknown error"}
          </p>
        </StepContent>
      );
    }

    switch (step) {
      case "preview":
        return renderPreviewData();
      case "insights":
        return renderInsightsData();
      case "dashboards":
        return renderDashboardsData();
      case "next-steps":
        return renderNextStepsData();
      default:
        return (
          <StepContent>
            <p>Analysis Completed successfully!</p>
            {data && (
              <pre
                style={{
                  fontSize: "0.8rem",
                  overflow: "auto",
                  maxHeight: "200px",
                  background: "rgba(255, 255, 255, 0.05)",
                  padding: "0.5rem",
                  borderRadius: "4px",
                }}
              >
                {JSON.stringify(data, null, 2)}
              </pre>
            )}
          </StepContent>
        );
    }
  };

  return (
    <StepContainer>
      <StepHeader>
        <StepIcon status={status}>{getStepIcon(step)}</StepIcon>
        <div>
          <StepTitle>
            {step.charAt(0).toUpperCase() + step.slice(1).replace("-", " ")}
          </StepTitle>
          <StepStatus status={status}>
            {getStatusIcon(status)}
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </StepStatus>
        </div>
      </StepHeader>
      {renderStepContent()}
    </StepContainer>
  );
};

export default AnalysisStepRenderer;
